import { GlobalContext } from '@/model/globalcontext.model';
import router from '@/router/index';
import { appSectionService } from '@/services/app-section.service';
import store from '@/store/index';
import { registerAdminPushNotifications } from '@/services/push.service';
import { clearClientSessionAndRedirectGuest } from '@/utils/auth-session-clear';
import { getClientDeviceMeta } from '@/utils/client-device-meta';
import { isNetworkConnected } from '@/utils/network-status';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { isPlatform } from '@ionic/vue';
import axios, { isAxiosError } from 'axios';

const LOCAL_AVATAR_FILE = 'liga-avatar.jpeg';

export default class AccountService {
  private globalContext?: GlobalContext;
  private localAvatarSyncInFlight = false;

  constructor(globalContext?: GlobalContext) {
    this.init();
    this.globalContext = globalContext;
  }

  public init(): void {
    this.retrieveProfiles();
  }

  private markOnline(): void {
    if (this.globalContext?.offlineMode) {
      this.globalContext.offlineMode = false;
    }
  }

  /** Offline только при реальной потере сети и наличии JWT. */
  private async enterOfflineModeIfDisconnected(): Promise<boolean> {
    if (!this.globalContext) return false;
    const connected = await isNetworkConnected();
    if (connected) return false;
    const checkJwt = await Preferences.get({ key: 'jwt' });
    if (!checkJwt.value?.length) return false;
    this.globalContext.offlineMode = true;
    return true;
  }

  private setOfflineMode(): void {
    void this.enterOfflineModeIfDisconnected().then((wentOffline) => {
      if (wentOffline) {
        // Не уводить со страницы, пока открыта любая блокирующая модалка (Android 15+ WebView crash).
        if (this.globalContext?.blockingConsentOpen) {
          return;
        }
        const path = router.currentRoute.value?.path || '';
        // /today → /showcase: лишний remount витрины; login не трогаем.
        if (
          path === '/showcase' ||
          path.startsWith('/showcase') ||
          path === '/today' ||
          path === '/login' ||
          path.startsWith('/login')
        ) {
          return;
        }
        router.replace('/today');
      }
    });
  }

  /** Clear auth state; при сети — витрина, без сети — вход. */
  private clearSessionAndRedirectToLogin(sessionExpired = true): void {
    void clearClientSessionAndRedirectGuest(sessionExpired);
  }

  /** Try to refresh JWT via API; returns new token or null. Saves token on success. */
  private async tryRefreshToken(currentToken: string | null): Promise<string | null> {
    if (!currentToken?.trim()) return null;
    const baseUrl = process.env.VUE_APP_API_URL;
    try {
      const res = await CapacitorHttp.post({
        url: `${baseUrl}api/refresh-token`,
        headers: { Authorization: `Bearer ${currentToken}` },
        data: {},
      });
      if (res.status !== 200) return null;
      const newJwt = (res.data as { id_token?: string })?.id_token ?? res.headers?.Authorization?.replace(/^Bearer\s+/i, '') ?? null;
      if (!newJwt) return null;
      await Preferences.set({ key: 'jwt', value: newJwt });
      localStorage.setItem('jhi-authenticationToken', newJwt);
      sessionStorage.removeItem('jhi-authenticationToken');
      return newJwt;
    } catch {
      return null;
    }
  }

  public async retrieveProfiles(): Promise<boolean> {
    if (this.globalContext && this.globalContext.offlineMode) {
      return new Promise(resolve => {
        resolve(true);
      });
    }

    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;

      const timeout = setTimeout(() => {
        this.setOfflineMode();
      }, 15000);

      return new Promise(resolve => {
        CapacitorHttp.request({
          method: 'GET',
          url: `${process.env.VUE_APP_API_URL}management/info`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
        }).then(async response => {
          if (response.status === 401) {
            const newToken = await this.tryRefreshToken(token);
            if (newToken) {
              const retry = await CapacitorHttp.request({
                method: 'GET',
                url: `${process.env.VUE_APP_API_URL}management/info`,
                headers: { Authorization: `Bearer ${newToken}` },
                readTimeout: 60000,
              });
              if (retry.status === 1001) this.retrieveProfiles();
              if (retry.data?.activeProfiles) store.commit('setActiveProfiles', retry.data['activeProfiles']);
              this.markOnline();
              resolve(true);
              return;
            }
            this.clearSessionAndRedirectToLogin(true);
            resolve(false);
            return;
          }
          if (response.status === 1001) {
            this.retrieveProfiles();
          }

          if (response.data && response.data.activeProfiles) {
            store.commit('setActiveProfiles', response.data['activeProfiles']);
          }
          this.markOnline();
          resolve(true);
        }).catch(async () => {
          if (token && (await this.enterOfflineModeIfDisconnected())) {
            resolve(true);
          } else {
            resolve(false);
          }
        }).finally(() => {
          clearTimeout(timeout);
        })
      });
    } else {
      return new Promise(resolve => {
        axios
          .get('management/info')
          .then(response => {
            if (response.data && response.data.activeProfiles) {
              store.commit('setActiveProfiles', response.data['activeProfiles']);
            }
            this.markOnline();
            resolve(true);
          })
          .catch((err) => {
            console.log(err);
            router.replace('/badconnection');
            resolve(false);
          });
      });
    }
  }

  public async retrieveAccount(): Promise<boolean> {
    if (this.globalContext && this.globalContext.offlineMode) {
      return new Promise(resolve => {
        resolve(true);
      });
    }

    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) {
        return false;
      }

      return new Promise(resolve => {
        CapacitorHttp.request({
          method: 'GET',
          url: `${process.env.VUE_APP_API_URL}api/account`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
        }).then(async response => {
          if (response.status === 401) {
            const newToken = await this.tryRefreshToken(token);
            if (newToken) {
              const retry = await CapacitorHttp.request({
                method: 'GET',
                url: `${process.env.VUE_APP_API_URL}api/account`,
                headers: { Authorization: `Bearer ${newToken}` },
                readTimeout: 60000,
              });
              if (retry.status === 200 && retry.data) {
                store.commit('authenticate');
                const account = retry.data;
                if (account?.authorities) {
                  await Preferences.set({ key: 'cached-authorities', value: JSON.stringify(account.authorities) });
                }
                store.commit('authenticated', account);
                await registerAdminPushNotifications(account.authorities);
                await appSectionService.loadSections(true);
                void this.syncLocalAvatarIfNeeded();
                void this.ensureSession();
                this.markOnline();
                resolve(true);
                return;
              }
            }
            this.clearSessionAndRedirectToLogin(true);
            resolve(false);
            return;
          }
          store.commit('authenticate');
          const account = response.data;
          if (account) {
            store.commit('authenticated', account);
            // Сохраняем роли в localStorage для офлайн-режима
            if (account.authorities && Array.isArray(account.authorities)) {
              await Preferences.set({
                key: 'cached-authorities',
                value: JSON.stringify(account.authorities),
              });
            }
              await registerAdminPushNotifications(account.authorities);
              await appSectionService.loadSections(true);
            void this.syncLocalAvatarIfNeeded();
            void this.ensureSession();
            this.markOnline();
            resolve(true);
          } else {
            Preferences.remove({ key: 'jwt' });
            store.commit('logout');
            resolve(false);
          }
        }).catch(async () => {
          if (await this.enterOfflineModeIfDisconnected()) {
            resolve(true);
          } else {
            resolve(false);
          }
        })
      });
    } else {
      return new Promise(resolve => {
        axios
          .get('api/account')
          .then(async response => {
            store.commit('authenticate');
            const account = response.data;
            if (account) {
              store.commit('authenticated', account);
              // Сохраняем роли в localStorage для офлайн-режима
              if (account.authorities && Array.isArray(account.authorities)) {
                localStorage.setItem('cached-authorities', JSON.stringify(account.authorities));
              }
              await registerAdminPushNotifications(account.authorities);
              await appSectionService.loadSections(true);
              void this.syncLocalAvatarIfNeeded();
              void this.ensureSession();
            } else {
              store.commit('logout');
            }
            this.markOnline();
            resolve(true);
          })
          .catch(async (err: unknown) => {
            if (isAxiosError(err) && err.response?.status === 401) {
              resolve(false);
              return;
            }
            if (await this.enterOfflineModeIfDisconnected()) {
              resolve(true);
            } else {
              resolve(false);
            }
          });
      });
    }
  }

  public async hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
    if (this.globalContext && this.globalContext.offlineMode) {
      // В офлайн-режиме проверяем кешированные роли
      const cachedAuthorities = await this.getCachedAuthorities();
      if (typeof authorities === 'string') {
        authorities = [authorities];
      }
      
      for (const authority of authorities) {
        if (cachedAuthorities.includes(authority)) {
          return Promise.resolve(true);
        }
      }
      return Promise.resolve(false);
    }

    if (typeof authorities === 'string') {
      authorities = [authorities];
    }

    if (!this.authenticated || !this.userAuthorities) {
      let token;
      if (isPlatform('ios') || isPlatform('android')) {
        const tokenQuery = await Preferences.get({ key: 'jwt' });
        token = tokenQuery.value;
      } else {
        token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
        if (token)
          await Preferences.set({
            key: 'jwt',
            value: token,
          });
      }
      if (!store.getters.account && !store.getters.logon && token) {
        return this.retrieveAccount();
      } else {
        return Promise.resolve(false);
      }
    }

    for (const authority of authorities) {
      if (this.userAuthorities.includes(authority)) {
        return Promise.resolve(true);
      }
    }

    return Promise.resolve(false);
  }

  private async getCachedAuthorities(): Promise<string[]> {
    try {
      if (isPlatform('ios') || isPlatform('android')) {
        const cached = await Preferences.get({ key: 'cached-authorities' });
        if (cached.value) {
          return JSON.parse(cached.value);
        }
      } else {
        const cached = localStorage.getItem('cached-authorities');
        if (cached) {
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      console.error('Error reading cached authorities:', error);
    }
    return [];
  }

  public get authenticated(): boolean {
    return store.getters.authenticated;
  }

  public get userAuthorities(): any {
    return store.getters.account.authorities;
  }

  /** Перевыпустить JWT из ролей в БД и обновить account/sections (после покупки с gift-ролями). */
  public async refreshAuthoritiesAfterPurchase(): Promise<boolean> {
    if (this.globalContext?.offlineMode) return false;
    try {
      let newToken: string | null = null;
      if (isPlatform('ios') || isPlatform('android')) {
        const { value: token } = await Preferences.get({ key: 'jwt' });
        if (!token) return false;
        const res = await CapacitorHttp.request({
          method: 'POST',
          url: `${process.env.VUE_APP_API_URL}api/account/refresh-authorities`,
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          data: {},
          readTimeout: 15000,
        });
        if (res.status !== 200) return false;
        const body = res.data as { id_token?: string };
        newToken =
          body?.id_token ??
          (typeof (res.headers as Record<string, string>)?.Authorization === 'string'
            ? (res.headers as Record<string, string>).Authorization.replace(/^Bearer\s+/i, '')
            : null) ??
          (typeof (res.headers as Record<string, string>)?.authorization === 'string'
            ? (res.headers as Record<string, string>).authorization.replace(/^Bearer\s+/i, '')
            : null);
        if (newToken) {
          await Preferences.set({ key: 'jwt', value: newToken });
          localStorage.setItem('jhi-authenticationToken', newToken);
          sessionStorage.removeItem('jhi-authenticationToken');
        }
      } else {
        const token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
        if (!token) return false;
        const { data, headers } = await axios.post<{ id_token?: string }>(
          'api/account/refresh-authorities',
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        newToken =
          data?.id_token ||
          (typeof headers?.authorization === 'string' ? headers.authorization.replace(/^Bearer\s+/i, '') : null) ||
          (typeof (headers as Record<string, string>)?.Authorization === 'string'
            ? (headers as Record<string, string>).Authorization.replace(/^Bearer\s+/i, '')
            : null);
        if (newToken) {
          localStorage.setItem('jhi-authenticationToken', newToken);
          sessionStorage.removeItem('jhi-authenticationToken');
          await Preferences.set({ key: 'jwt', value: newToken });
        }
      }
      if (!newToken) return false;
      return await this.retrieveAccount();
    } catch (e) {
      console.warn('refreshAuthoritiesAfterPurchase failed', e);
      return false;
    }
  }

  /** Убедиться, что текущее устройство имеет сессию; при необходимости создаёт и возвращает новый токен. */
  public async ensureSession(): Promise<string | null> {
    if (this.globalContext?.offlineMode) return null;
    const meta = await getClientDeviceMeta();
    const payload = {
      deviceName: meta.deviceName || undefined,
      platform: meta.platform || undefined,
      appVersion: meta.appVersion || undefined,
      appBuild: meta.appBuild || undefined,
    };
    if (isPlatform('ios') || isPlatform('android')) {
      const { value: token } = await Preferences.get({ key: 'jwt' });
      if (!token) return null;
      const res = await CapacitorHttp.request({
        method: 'POST',
        url: `${process.env.VUE_APP_API_URL}api/account/ensure-session`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: payload,
        readTimeout: 10000,
      });
      if (res.status !== 200) return null;
      const body = res.data as { id_token?: string };
      const newToken = body?.id_token ?? (res.headers as Record<string, string>)?.['authorization']?.replace(/^Bearer\s+/i, '');
      if (newToken) {
        await Preferences.set({ key: 'jwt', value: newToken });
        localStorage.setItem('jhi-authenticationToken', newToken);
        sessionStorage.removeItem('jhi-authenticationToken');
        return newToken;
      }
      return null;
    }
    const token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
    if (!token) return null;
    const { data } = await axios.post<{ id_token?: string }>(
      'api/account/ensure-session',
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const newToken = data?.id_token;
    if (newToken) {
      localStorage.setItem('jhi-authenticationToken', newToken);
      sessionStorage.removeItem('jhi-authenticationToken');
      return newToken;
    }
    return null;
  }

  /** Список авторизаций (устройств) текущего пользователя */
  public async getSessions(): Promise<{ id: number; deviceName: string; createdAt: string; lastUsedAt: string | null }[]> {
    if (this.globalContext?.offlineMode) return [];
    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) return [];
      const res = await CapacitorHttp.request({
        method: 'GET',
        url: `${process.env.VUE_APP_API_URL}api/account/sessions`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      if (res.status !== 200 || !Array.isArray(res.data)) return [];
      return res.data;
    }
    const { data } = await axios.get<{ id: number; deviceName: string; createdAt: string; lastUsedAt: string | null }[]>('api/account/sessions');
    return Array.isArray(data) ? data : [];
  }

  /** Завершить сессию (выйти с устройства) по id сессии */
  public async revokeSession(sessionId: number): Promise<boolean> {
    if (this.globalContext?.offlineMode) return false;
    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) return false;
      const res = await CapacitorHttp.request({
        method: 'DELETE',
        url: `${process.env.VUE_APP_API_URL}api/account/sessions/${sessionId}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      return res.status === 204;
    }
    const res = await axios.delete(`api/account/sessions/${sessionId}`);
    return res.status === 204;
  }

  /**
   * Если на сервере ещё нет imageUrl, а локально есть liga-avatar.jpeg —
   * один раз догружаем (миграция после обновления приложения).
   */
  public async syncLocalAvatarIfNeeded(): Promise<void> {
    if (this.localAvatarSyncInFlight) return;
    if (this.globalContext?.offlineMode) return;
    const account = store.getters.account;
    if (!account?.id || (account.imageUrl && String(account.imageUrl).trim())) return;

    this.localAvatarSyncInFlight = true;
    try {
      const listing = await Filesystem.readdir({ path: '', directory: Directory.Data });
      const hasLocal = listing.files?.some(f => f.name === LOCAL_AVATAR_FILE);
      if (!hasLocal) return;

      const { uri } = await Filesystem.getUri({
        path: LOCAL_AVATAR_FILE,
        directory: Directory.Data,
      });
      if (!uri) return;
      const src = Capacitor.convertFileSrc(uri);
      const blob = await fetch(src).then(r => r.blob());
      if (!blob || blob.size < 32) return;

      const imageUrl = await this.uploadAvatar(blob, 'avatar.jpg');
      if (!imageUrl) return;
      const latest = store.getters.account;
      if (latest) {
        store.commit('authenticated', { ...latest, imageUrl });
      }
    } catch (e) {
      console.warn('syncLocalAvatarIfNeeded skipped', e);
    } finally {
      this.localAvatarSyncInFlight = false;
    }
  }

  /** Телефон из профиля Account (тот же, что при оплате событий/подписок). */
  public async getMyPhone(): Promise<string> {
    if (this.globalContext?.offlineMode) return '';
    try {
      if (isPlatform('ios') || isPlatform('android')) {
        const tokenQuery = await Preferences.get({ key: 'jwt' });
        const token = tokenQuery.value;
        if (!token) return '';
        const res = await CapacitorHttp.get({
          url: `${process.env.VUE_APP_API_URL}api/liga-events/my-phone`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
        });
        if (res.status < 200 || res.status >= 300) return '';
        return (res.data as { phone?: string })?.phone ?? '';
      }
      const { data } = await axios.get<{ phone?: string }>('api/liga-events/my-phone');
      return data?.phone ?? '';
    } catch (e) {
      console.error('getMyPhone', e);
      return '';
    }
  }

  /**
   * Обновление имени/фамилии текущего пользователя (POST /api/account).
   * Остальные поля (login, email, langKey, imageUrl) отправляются как есть,
   * чтобы бэкенд не затёр их null.
   */
  public async updateProfile(firstName: string, lastName: string): Promise<boolean> {
    if (this.globalContext?.offlineMode) return false;
    const account = store.getters.account;
    if (!account?.login) return false;

    const payload = {
      login: account.login,
      email: account.email,
      firstName: firstName?.trim() || '',
      lastName: lastName?.trim() || '',
      langKey: account.langKey || 'ru',
      imageUrl: account.imageUrl || null,
    };

    try {
      if (isPlatform('ios') || isPlatform('android')) {
        const tokenQuery = await Preferences.get({ key: 'jwt' });
        const token = tokenQuery.value;
        if (!token) return false;
        const res = await CapacitorHttp.post({
          url: `${process.env.VUE_APP_API_URL}api/account`,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          data: payload,
          readTimeout: 60000,
        });
        if (res.status < 200 || res.status >= 300) return false;
      } else {
        await axios.post('api/account', payload);
      }
      store.commit('authenticated', {
        ...account,
        firstName: payload.firstName,
        lastName: payload.lastName,
      });
      return true;
    } catch (e) {
      console.error('updateProfile', e);
      return false;
    }
  }

  /**
   * Загрузка аватара на сервер (multipart).
   * @returns относительный imageUrl (api/account/avatars/{id}) или null
   */
  public async uploadAvatar(file: Blob | File, filename = 'avatar.jpg'): Promise<string | null> {
    if (this.globalContext?.offlineMode) return null;
    const form = new FormData();
    form.append('file', file, filename);

    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) return null;
      const url = `${process.env.VUE_APP_API_URL}api/account/avatar`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { imageUrl?: string };
      return data?.imageUrl || null;
    }

    const { data } = await axios.post<{ imageUrl: string }>('api/account/avatar', form);
    return data?.imageUrl || null;
  }

  /** Сохранить JWT из ответа authenticate / otp/verify и подтянуть аккаунт. */
  public async persistJwtFromAuthResponse(response: {
    headers?: Record<string, string>;
    data?: unknown;
  }): Promise<void> {
    const headers = response.headers || {};
    const bearerToken =
      headers.Authorization ||
      headers.authorization ||
      headers['Authorization'] ||
      (typeof (response.data as { id_token?: string })?.id_token === 'string'
        ? `Bearer ${(response.data as { id_token: string }).id_token}`
        : '');
    if (!(bearerToken && bearerToken.slice(0, 7) === 'Bearer ')) {
      throw new Error('Bad jwt');
    }
    const jwt = bearerToken.slice(7);
    if (!jwt) throw new Error('Bad jwt');
    await Preferences.set({ key: 'jwt', value: jwt });
    localStorage.setItem('jhi-authenticationToken', jwt);
    sessionStorage.removeItem('jhi-authenticationToken');
    this.retrieveProfiles();
    await this.retrieveAccount();
  }

  public async deleteAccount(): Promise<boolean> {
    if (this.globalContext && this.globalContext.offlineMode) {
      return new Promise(resolve => {
        resolve(false);
      });
    }

    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) {
        return false;
      }

      return new Promise(resolve => {
        CapacitorHttp.request({
          method: 'DELETE',
          url: `${process.env.VUE_APP_API_URL}api/account`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
        }).then(async () => {
          await Preferences.remove({ key: 'jwt' });
          await Preferences.remove({ key: 'cached-authorities' });
          await appSectionService.clearCache();
          store.commit('logout');
          resolve(true);
        }).catch(() => {
          resolve(false);
        })
      });
    } else {
      return new Promise(resolve => {
        axios
          .delete('api/account')
          .then(async () => {
            await Preferences.remove({ key: 'jwt' });
            localStorage.removeItem('jhi-authenticationToken');
            sessionStorage.removeItem('jhi-authenticationToken');
            localStorage.removeItem('cached-authorities');
            await appSectionService.clearCache();
            store.commit('logout');
            resolve(true);
          })
          .catch(() => {
            resolve(false);
          });
      });
    }
  }

}
